import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const { FiCheck, FiGlobe, FiPalette, FiFileText, FiLayers } = FiIcons;

const Pricing = () => {
  useEffect(() => {
    // Handle hash-based navigation
    const handleHashNavigation = () => {
      const hash = window.location.hash.replace('#/pricing#', '').replace('#', '');
      if (hash) {
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
          }
        }, 100);
      }
    };

    handleHashNavigation();
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashNavigation);
    
    return () => {
      window.removeEventListener('hashchange', handleHashNavigation);
    };
  }, []);

  const hostingPlans = [
    {
      name: 'Lite',
      price: 'R100',
      period: '/mo*',
      description: 'Perfect for small websites and personal projects',
      features: [
        '1 Website',
        '500 MB SSD Storage',
        '2 Email accounts',
        '15 GB Traffic/mo',
        'Free website builder',
        'Custom control panel',
        'Free SSL certificate'
      ],
      buttonText: 'Get started',
      buttonStyle: 'border border-gray-300 text-gray-700 hover:border-primary-500 hover:text-primary-500'
    },
    {
      name: 'Standard',
      price: 'R160',
      period: '/mo*',
      description: 'Ideal for growing businesses with moderate traffic',
      features: [
        '1 Website',
        '1000 MB SSD Storage',
        '5 Email accounts',
        '30 GB Traffic/mo',
        'Free website builder',
        'Custom control panel',
        'Free SSL certificate'
      ],
      buttonText: 'Get started',
      buttonStyle: 'border border-gray-300 text-gray-700 hover:border-primary-500 hover:text-primary-500'
    },
    {
      name: 'Premium',
      price: 'R258',
      period: '/mo*',
      description: 'Great for multiple websites and higher traffic',
      popular: true,
      features: [
        '4 Websites',
        '5 GB SSD Storage',
        '10 Email accounts',
        '150 GB Traffic/mo',
        'Free website builder',
        'cPanel control panel',
        'Free SSL certificates'
      ],
      buttonText: 'Get started',
      buttonStyle: 'bg-primary-500 text-white hover:bg-primary-600'
    },
    {
      name: 'Business',
      price: 'R600',
      period: '/mo*',
      description: 'Perfect for established businesses with high traffic',
      features: [
        '8 Websites',
        '20 GB SSD Storage',
        '15 Email accounts',
        '600 GB Traffic/mo',
        'Free website builder',
        'cPanel control panel',
        'Free SSL certificates'
      ],
      buttonText: 'Get started',
      buttonStyle: 'border border-gray-300 text-gray-700 hover:border-primary-500 hover:text-primary-500'
    }
  ];

  const designPackages = [
    {
      name: '1 Page website',
      price: 'R1,500',
      period: 'once-off',
      description: 'Perfect for landing pages and simple business presence',
      features: [
        'Client content integration',
        'Static layout design',
        '1 page (1024 × 1536 px)',
        'Mobile responsive',
        'Basic SEO setup',
        '2 revisions included',
        'Hosting excluded'
      ],
      icon: FiFileText
    },
    {
      name: '3 Page website',
      price: 'R4,500',
      period: 'once-off',
      description: 'Ideal for small businesses and professional portfolios',
      features: [
        'Up to 3 pages',
        'Client-generated content',
        'Static layout design',
        '2 revisions included',
        'Mobile responsive',
        'SEO-friendly structure',
        'Hosting excluded'
      ],
      icon: FiGlobe,
      popular: true
    },
    {
      name: '6 Page website',
      price: 'R10,000',
      period: 'once-off',
      description: 'Comprehensive solution for established businesses',
      features: [
        'Up to 6 pages',
        'Client-generated content',
        'Static layout design',
        '2 revisions included',
        'Mobile responsive',
        'Advanced SEO setup',
        'Hosting excluded'
      ],
      icon: FiLayers
    }
  ];

  const additionalServices = [
    {
      name: 'Website content development',
      price: 'R600/hr',
      description: 'Professional content creation (excluding web design)'
    },
    {
      name: 'Logo design',
      price: 'R600',
      description: '2 revisions included'
    },
    {
      name: 'Letterhead design',
      price: 'R800',
      description: '2 revisions included'
    },
    {
      name: 'Email signature',
      price: 'R1,000',
      description: '2 revisions – 1 free email signature when hosting with Primehost'
    }
  ];

  const domainPricing = [
    {
      extension: '.co.za',
      price: 'R199',
      period: '/year',
      description: 'South African business domains'
    },
    {
      extension: '.org.za',
      price: 'R199',
      period: '/year',
      description: 'Non-profit organizations'
    },
    {
      extension: '.com',
      price: 'R300',
      period: '/year',
      description: 'International commercial domains'
    },
    {
      extension: '.net',
      price: 'R350',
      period: '/year',
      description: 'Network and tech companies'
    },
    {
      extension: '.org',
      price: 'R320',
      period: '/year',
      description: 'Organizations and non-profits'
    }
  ];

  const scrollToContactForm = () => {
    window.location.hash = '#/contact#contact-form';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section style={{
      paddingTop: '50px',
      paddingBottom: '50px'
    }} className="bg-gradient-to-br from-gray-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left"
            >
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                <span style={{ color: '#FF5841' }}>Transparent</span> pricing for professional digital services
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Choose the perfect plan for your business needs. No hidden fees, no surprises – just reliable services at competitive prices.
              </p>
              <div className="bg-primary-50 rounded-lg p-4 inline-block">
                <p className="text-primary-700 font-medium">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 inline mr-2" />
                  No hidden fees policy – what you see is what you pay
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="https://media.phst.co.za/file/ph/primehost-hosting-plans.jpg"
                alt="Professional pricing and business solutions"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Web Hosting Plans */}
      <section style={{
      paddingTop: '50px',
      paddingBottom: '50px'
    }} id="hosting-plans" className="py-20 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Web hosting plans
            </h2>
            <p className="text-xl text-gray-600">
              Reliable hosting solutions with 99.9% uptime guarantee
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {hostingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative bg-white rounded-2xl shadow-lg border-2 p-8 ${
                  plan.popular ? 'border-primary-500' : 'border-gray-100'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-primary-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most popular
                    </div>
                  </div>
                )}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600 ml-1">{plan.period}</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={scrollToContactForm}
                  className={`w-full py-3 px-4 rounded-lg font-semibold text-center transition-colors duration-200 ${plan.buttonStyle}`}
                >
                  {plan.buttonText}
                </button>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <p className="text-gray-600">
              *Billed annually in advance. Cancel anytime.{' '}
              <button onClick={scrollToContactForm} className="text-primary-500 hover:text-primary-600">
                Contact us to discuss tailored solutions.
              </button>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Web Design Packages */}
      <section id="design-packages" className="py-20 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Web design packages
            </h2>
            <p className="text-xl text-gray-600">
              Professional website design solutions for every business need (once-off pricing)
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {designPackages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative bg-white rounded-2xl shadow-lg p-8 ${
                  pkg.popular ? 'border-2 border-primary-500' : 'border border-gray-100'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-primary-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most popular
                    </div>
                  </div>
                )}
                <div className="text-center mb-8">
                  <div className="bg-primary-100 p-4 rounded-2xl w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={pkg.icon} className="w-8 h-8 text-primary-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{pkg.description}</p>
                  <div className="flex items-baseline justify-center">
                    <span className="text-3xl font-bold text-primary-500">{pkg.price}</span>
                    <span className="text-gray-600 ml-2 text-sm">{pkg.period}</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={scrollToContactForm}
                  className={`w-full py-3 px-4 rounded-lg font-semibold text-center transition-colors duration-200 ${
                    pkg.popular
                      ? 'bg-primary-500 text-white hover:bg-primary-600'
                      : 'border border-gray-300 text-gray-700 hover:border-primary-500 hover:text-primary-500'
                  }`}
                >
                  Get started
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Additional services
            </h2>
            <p className="text-xl text-gray-600">
              Comprehensive design and content services to complete your brand
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-xl p-6 flex justify-between items-center"
              >
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {service.name}
                  </h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </div>
                <div className="text-xl font-bold text-primary-500">
                  {service.price}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Domain Registration Section */}
      <section id="domains" className="py-20 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Domain registration pricing
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Secure your perfect domain name with competitive pricing
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {domainPricing.map((domain, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 text-center border border-gray-200 hover:border-primary-300 transition-colors duration-200"
              >
                <div className="text-2xl font-bold text-gray-900 mb-2">
                  {domain.extension}
                </div>
                <div className="text-3xl font-bold text-primary-500 mb-2">
                  {domain.price}
                  <span className="text-base text-gray-600">{domain.period}</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">{domain.description}</p>
                <button
                  onClick={scrollToContactForm}
                  className="w-full bg-primary-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-600 transition-colors duration-200"
                >
                  Register now
                </button>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <p className="text-gray-600">
              Need help choosing the right domain?{' '}
              <button onClick={scrollToContactForm} className="text-primary-500 hover:text-primary-600">
                Contact our experts for guidance.
              </button>
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: '#D14932' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to get started?
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Choose your perfect plan or contact us for a custom solution tailored to your business needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={scrollToContactForm}
                className="bg-white text-red-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                Get started today
              </button>
              <Link
                to="/services"
                className="border border-red-300 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-600 transition-colors duration-200"
              >
                Learn more
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Pricing;